import React, { useEffect, useLayoutEffect, useRef, useState, useCallback } from "react";
import {
  UtensilsCrossed, Stethoscope, ShoppingBag, Building2, Hotel, GraduationCap,
  Factory, Coffee, Scissors, HeartPulse, BookOpen, Dumbbell, Home as HomeIcon,
  Store, ShoppingCart, X
} from "lucide-react";

const INDUSTRIES = [
  { name: "Restaurant", Icon: UtensilsCrossed, color: "#FF6000", challenges: "Slow service, low repeat visits, no online presence.", solutions: "AI Voice Reception · WhatsApp ordering · Loyalty automation", roi: "+184%" },
  { name: "Hospital", Icon: Stethoscope, color: "#00F0FF", challenges: "Missed appointments, manual triage, limited follow-up.", solutions: "AI Scheduler · Triage Chatbot · Post-visit care AI", roi: "+318%" },
  { name: "Retail Store", Icon: ShoppingBag, color: "#FF007F", challenges: "Cart abandonment, no remarketing, weak insight.", solutions: "Predictive AI · WhatsApp recovery · Smart inventory", roi: "+289%" },
  { name: "Office", Icon: Building2, color: "#B200FF", challenges: "Manual admin, lost emails, no automation.", solutions: "AI Inbox · Meeting agent · Document copilots", roi: "+220%" },
  { name: "Hotel", Icon: Hotel, color: "#FF6000", challenges: "Static pricing, low direct bookings.", solutions: "Dynamic pricing · Concierge avatar · Upsell AI", roi: "+256%" },
  { name: "School", Icon: GraduationCap, color: "#00F0FF", challenges: "Parent disengagement, manual progress reports.", solutions: "AI Tutor · Parent updates · Enrollment bot", roi: "+201%" },
  { name: "Factory", Icon: Factory, color: "#B200FF", challenges: "Downtime, manual QA, supply guesswork.", solutions: "Predictive maintenance · Vision QA · Demand AI", roi: "+312%" },
  { name: "Cafe", Icon: Coffee, color: "#FFB400", challenges: "Walk-in only, no loyalty data, peak chaos.", solutions: "QR ordering · Loyalty AI · Demand forecasting", roi: "+162%" },
  { name: "Salon", Icon: Scissors, color: "#FF007F", challenges: "No-shows, manual bookings, lost upsells.", solutions: "AI Booking · WhatsApp reminders · Upsell agent", roi: "+178%" },
  { name: "Clinic", Icon: HeartPulse, color: "#00F0FF", challenges: "Phone-only intake, lost leads, slow follow-ups.", solutions: "AI Receptionist · Patient triage · Recall automation", roi: "+245%" },
  { name: "Tuition Centre", Icon: BookOpen, color: "#7000FF", challenges: "Parent engagement, manual enrolments, churn.", solutions: "AI Enrolment bot · Progress reports · Tutor copilot", roi: "+193%" },
  { name: "Fitness Centre", Icon: Dumbbell, color: "#FF6000", challenges: "Member churn, weak retention, no personalization.", solutions: "AI Coach · Churn predictor · Class fill automation", roi: "+210%" },
  { name: "Real Estate", Icon: HomeIcon, color: "#B200FF", challenges: "Cold leads, slow follow-up, scattered listings.", solutions: "AI Lead Qualifier · Voice agent · Listing matchmaker", roi: "+274%" },
  { name: "Supermarket", Icon: Store, color: "#00F0FF", challenges: "Inventory blind spots, queue lines, dated promos.", solutions: "Demand AI · Smart shelf vision · Personalized offers", roi: "+228%" },
  { name: "Mini Mart", Icon: ShoppingCart, color: "#FFB400", challenges: "Manual restocks, no loyalty, thin margins.", solutions: "AI Restock · WhatsApp loyalty · Dynamic pricing", roi: "+196%" },
];

const POPUP_WIDTH = 340;

function IndustryCard({ ind, index, isActive, onSelect, innerRef }) {
  const { Icon } = ind;
  return (
    <button
      ref={innerRef}
      onClick={(e) => { e.stopPropagation(); onSelect(index); }}
      data-testid={`industry-${ind.name.toLowerCase().replace(/\s+/g, "-")}`}
      className={`group relative h-40 rounded-2xl overflow-hidden border transition-all duration-300 ${
        isActive ? "border-cyan-400/70 glow-cyan scale-[1.03] z-10" : "border-white/10 hover:border-white/30 hover:scale-[1.01]"
      }`}
      style={{ background: `linear-gradient(180deg, ${ind.color}22 0%, rgba(5,8,22,0.92) 75%)` }}
    >
      <div className="absolute inset-x-0 bottom-0 h-2/3" style={{ background: `linear-gradient(180deg, transparent, ${ind.color}33)` }} />
      <div className="absolute top-3 left-3 font-mono text-[10px] uppercase tracking-widest text-white/55">{String(index + 1).padStart(2, "0")}</div>
      <div className="absolute inset-0 grid place-items-center">
        <div
          className="w-14 h-14 rounded-2xl grid place-items-center transition-transform group-hover:scale-110 group-hover:-translate-y-1"
          style={{
            background: `radial-gradient(circle at 30% 30%, ${ind.color}55, ${ind.color}22 70%)`,
            boxShadow: `0 0 24px ${ind.color}55, inset 0 0 14px ${ind.color}44`,
          }}
        >
          <Icon size={26} color={ind.color} strokeWidth={1.75} />
        </div>
      </div>
      <div className="absolute bottom-3 left-3 right-3 font-[Outfit] font-semibold text-sm">{ind.name}</div>
    </button>
  );
}

function IndustryPopup({ industry, style, popupRef, onClose }) {
  const { Icon } = industry;
  const arrowSide = style.side === "left" ? "right" : "left"; // arrow on tile-facing side
  return (
    <div
      ref={popupRef}
      role="dialog"
      data-testid="industry-detail-popup"
      className="z-30 rounded-2xl glass holo-border p-5 shadow-2xl animate-[fadeInPop_180ms_ease-out]"
      style={{
        position: "absolute",
        width: POPUP_WIDTH,
        left: style.left,
        top: style.top,
        boxShadow: `0 20px 60px rgba(0,0,0,0.55), 0 0 40px ${industry.color}44`,
      }}
      onClick={(e) => e.stopPropagation()}
    >
      {/* Arrow pointer to tile (side-facing) */}
      <div
        className="absolute w-3 h-3 rotate-45 border-l border-b"
        style={{
          top: style.arrowTop,
          [arrowSide]: -7,
          background: "rgba(10, 15, 36, 0.85)",
          borderColor: `${industry.color}88`,
          transform: arrowSide === "left" ? "rotate(45deg)" : "rotate(-135deg)",
        }}
      />

      <button
        onClick={onClose}
        data-testid="industry-popup-close"
        className="absolute top-2 right-2 w-7 h-7 grid place-items-center rounded-full text-white/60 hover:text-white hover:bg-white/10 transition"
        aria-label="Close"
      >
        <X size={14} />
      </button>

      <div className="flex items-center gap-3">
        <div
          className="w-12 h-12 rounded-xl grid place-items-center shrink-0"
          style={{
            background: `radial-gradient(circle at 30% 30%, ${industry.color}55, ${industry.color}11 75%)`,
            boxShadow: `0 0 24px ${industry.color}66, inset 0 0 12px ${industry.color}55`,
          }}
        >
          <Icon size={24} color={industry.color} strokeWidth={1.5} />
        </div>
        <div className="min-w-0">
          <p className="font-mono text-[9px] uppercase tracking-widest text-white/45">Industry</p>
          <h3 className="text-xl font-[Outfit] font-bold truncate" style={{ color: industry.color }}>{industry.name}</h3>
        </div>
        <div
          className="ml-auto inline-flex px-2.5 py-1 rounded-full font-mono text-[10px] shrink-0"
          style={{ background: `${industry.color}22`, color: industry.color }}
        >
          ROI {industry.roi}
        </div>
      </div>

      <div className="mt-4">
        <p className="font-mono text-[9px] uppercase tracking-widest text-white/45">Common Challenges</p>
        <p className="mt-1.5 text-white/85 text-sm leading-relaxed">{industry.challenges}</p>
      </div>

      <div className="mt-3">
        <p className="font-mono text-[9px] uppercase tracking-widest text-white/45">AI Solutions</p>
        <p className="mt-1.5 text-white/85 text-sm leading-relaxed">{industry.solutions}</p>
      </div>
    </div>
  );
}

export default function Industries() {
  const [active, setActive] = useState(null); // null = closed
  const [popupStyle, setPopupStyle] = useState({ left: 0, top: 0, arrowTop: 20, side: "right" });

  const gridRef = useRef(null);
  const popupRef = useRef(null);
  const tileRefs = useRef([]);

  const computePosition = useCallback((index) => {
    const grid = gridRef.current;
    const tile = tileRefs.current[index];
    if (!grid || !tile) return;
    const gridRect = grid.getBoundingClientRect();
    const tileRect = tile.getBoundingClientRect();

    const GAP = 14;
    const tileLeftInGrid = tileRect.left - gridRect.left;
    const tileRightInGrid = tileRect.right - gridRect.left;
    const spaceRight = gridRect.width - tileRightInGrid;
    const spaceLeft = tileLeftInGrid;

    // Prefer right if enough space, else flip left
    let side = "right";
    let left;
    if (spaceRight >= POPUP_WIDTH + GAP) {
      side = "right";
      left = tileRightInGrid + GAP;
    } else if (spaceLeft >= POPUP_WIDTH + GAP) {
      side = "left";
      left = tileLeftInGrid - POPUP_WIDTH - GAP;
    } else {
      // Not enough on either side (very narrow screens) — center below the tile
      side = "right";
      const tileCenter = tileLeftInGrid + tileRect.width / 2;
      left = Math.max(8, Math.min(tileCenter - POPUP_WIDTH / 2, gridRect.width - POPUP_WIDTH - 8));
    }

    // Vertically align popup center with tile center, clamped within grid
    const tileCenterY = tileRect.top - gridRect.top + tileRect.height / 2;
    const popupHeightEstimate = 260;
    let top = tileCenterY - popupHeightEstimate / 2;
    top = Math.max(0, Math.min(top, gridRect.height - 40));

    // Arrow vertically points at tile center relative to popup
    const arrowTop = Math.max(14, Math.min(tileCenterY - top - 6, popupHeightEstimate - 26));

    setPopupStyle({ left, top, arrowTop, side });
  }, []);

  useLayoutEffect(() => {
    if (active !== null) computePosition(active);
  }, [active, computePosition]);

  useEffect(() => {
    if (active === null) return;
    const handleResize = () => computePosition(active);
    const handleClickOutside = (e) => {
      const tile = tileRefs.current[active];
      if (
        popupRef.current && !popupRef.current.contains(e.target) &&
        tile && !tile.contains(e.target)
      ) {
        setActive(null);
      }
    };
    const handleEsc = (e) => { if (e.key === "Escape") setActive(null); };

    window.addEventListener("resize", handleResize);
    window.addEventListener("scroll", handleResize, { passive: true });
    document.addEventListener("mousedown", handleClickOutside);
    document.addEventListener("keydown", handleEsc);
    return () => {
      window.removeEventListener("resize", handleResize);
      window.removeEventListener("scroll", handleResize);
      document.removeEventListener("mousedown", handleClickOutside);
      document.removeEventListener("keydown", handleEsc);
    };
  }, [active, computePosition]);

  const handleSelect = (index) => {
    setActive((prev) => (prev === index ? null : index));
  };

  return (
    <section id="industries" className="relative py-28 px-6" data-testid="industries-section">
      <div className="max-w-7xl mx-auto">
        <div className="text-center mb-14">
          <p className="font-mono text-[11px] tracking-[0.4em] text-cyan-300/80 mb-3">◆ INDUSTRIES</p>
          <h2 className="text-4xl sm:text-5xl font-[Outfit] font-bold">A city of <span className="text-gradient">AI-native businesses.</span></h2>
          <p className="mt-3 text-white/65 max-w-2xl mx-auto">Tap a tile to reveal how MUNNEXA AI transforms it.</p>
        </div>

        <div ref={gridRef} className="relative" data-testid="industries-grid-wrap">
          <div
            className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 xl:grid-cols-7 gap-3"
            data-testid="industries-grid"
          >
            {INDUSTRIES.map((ind, i) => (
              <IndustryCard
                key={ind.name}
                ind={ind}
                index={i}
                isActive={active === i}
                onSelect={handleSelect}
                innerRef={(el) => (tileRefs.current[i] = el)}
              />
            ))}
          </div>

          {active !== null && (
            <IndustryPopup
              industry={INDUSTRIES[active]}
              style={popupStyle}
              popupRef={popupRef}
              onClose={() => setActive(null)}
            />
          )}
        </div>

        <p className="mt-8 text-center font-mono text-[10px] tracking-widest text-white/40 uppercase">
          {active === null ? "◆ Select an industry to view its AI blueprint" : "◆ Click outside or press ESC to close"}
        </p>
      </div>

      {/* Popup animation */}
      <style>{`
        @keyframes fadeInPop {
          from { opacity: 0; transform: translateY(-6px) scale(0.98); }
          to   { opacity: 1; transform: translateY(0) scale(1); }
        }
      `}</style>
    </section>
  );
}
